'use client';

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Define user roles
export type UserRole = 'admin' | 'viewer' | 'guest';

// User type with role information
export interface UserWithRole extends FirebaseUser {
    role?: UserRole;
    permissions?: string[];
}

// Context type
interface AuthContextType {
    user: UserWithRole | null;
    loading: boolean;
    role: UserRole | null;
    permissions: string[];
    hasPermission: (permission: string) => boolean;
    isRole: (role: UserRole) => boolean;
    refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Role-based permissions mapping
const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
    admin: [
        'read:all',
        'write:all',
        'delete:all',
        'manage:users',
        'manage:experiments',
        'view:samples',
        'view:dashboard',
        'view:sample_records',
        'view:sample_groups',
        'view:sample_group_summary',
        'view:calendar',
        'view:daily_view',
        'view:experiment_design',
        'view:experiment_queue',
        'edit:experiments',
        'delete:experiments',
        'manage:calendar',
        'view:ltec_batches',
        'view:drug_stock',
        'view:surgeon_actions',
        'view:lab_activity',
    ],
    viewer: [
        'view:dashboard',
        'view:samples',
        'view:sample_records',
        'view:sample_group_summary',
        'view:calendar',
        'view:daily_view',
        'view:scheduling'
    ],
    guest: [
        'view:dashboard'
    ]
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<UserWithRole | null>(null);
    const [loading, setLoading] = useState(true);

    const getUserRole = async (firebaseUser: FirebaseUser): Promise<UserRole> => {
        try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            if (userDoc.exists()) {
                const userData = userDoc.data();
                return userData.role || 'guest';
            } else {
                // Create new user document with default role
                const defaultRole: UserRole = 'guest';
                await setDoc(doc(db, 'users', firebaseUser.uid), {
                    email: firebaseUser.email,
                    name: firebaseUser.displayName,
                    role: defaultRole,
                    createdAt: new Date(),
                    lastLogin: new Date()
                });
                return defaultRole;
            }
        } catch (error) {
            console.error('Error fetching user role:', error);
            return 'guest';
        }
    };

    const refreshUserRole = async () => {
        if (user) {
            const newRole = await getUserRole(user);
            setUser({ ...user, role: newRole, permissions: ROLE_PERMISSIONS[newRole] });
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const role = await getUserRole(firebaseUser);
                const userWithRole: UserWithRole = {
                    ...firebaseUser,
                    role,
                    permissions: ROLE_PERMISSIONS[role]
                };
                setUser(userWithRole);

                // Update last login
                try {
                    await setDoc(doc(db, 'users', firebaseUser.uid), {
                        lastLogin: new Date()
                    }, { merge: true });
                } catch (error) {
                    console.error('Failed to update last login time:', error);
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const hasPermission = (permission: string): boolean => {
        return user?.permissions?.includes(permission) || false;
    };

    const isRole = (role: UserRole): boolean => {
        return user?.role === role;
    };

    const value: AuthContextType = {
        user,
        loading,
        role: user?.role || null,
        permissions: user?.permissions || [],
        hasPermission,
        isRole,
        refreshUserRole,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
} 